import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Dispatch, connect } from 'react-redux';
import { IReportState, IApplicationState, AppActionCreators, ReportActionCreators, ICenterBillingReportResponse } from '@nkr/store/index';
import { ICenterBillingReport } from '@nkr/models/index';
import { HeaderWrapper } from './styles';
import { GridApi, ColumnApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams } from 'ag-grid-community';
import { FormControl, Button } from 'react-bootstrap';
import { FilterGroup } from './filter-group';
import Filter from './filter';
import Grid from './grid';
import { OrganType } from '@nkr/enums/organ-type';

const EpletRiskCutoff = {
    Low: 10
};

interface IOwnProps { }

interface IStateProps {
    reports: IReportState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getCenterBillingReport: (startDate: string, endDate: string, filterGroup: number, organType: OrganType | null) => Promise<ICenterBillingReportResponse>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    report: ICenterBillingReport[],
    startDate: string,
    endDate: string,
    filterGroup: FilterGroup,
    enableColumnFiltering: boolean,
    display: number[],
    organTypes: OrganType[],
    showDateWarning: boolean,
    gridApi: GridApi,
    columnApi: ColumnApi
}

class CenterBillingReport extends React.Component<TProps, IState> {
    readonly All: string = '- ALL -';
    readonly Yes: string = 'Yes';
    readonly No: string = 'No';

    readonly state: IState = {
        report: [],
        startDate: '',
        endDate: '',
        filterGroup: FilterGroup.Facilitated,
        enableColumnFiltering: false,
        display: [1, 2],
        organTypes: [OrganType.Kidney, OrganType.Liver],
        showDateWarning: false,
        gridApi: null,
        columnApi: null
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        });
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let defaultStartDate = '';
        let defaultEndDate = '';

        if (defaultStartDate == '' || defaultEndDate == '') {
            defaultStartDate = moment().startOf('month').format('YYYY-MM-DD');
            defaultEndDate = moment().endOf('month').format('YYYY-MM-DD');
        } else {
            defaultStartDate = this.state.startDate;
            defaultEndDate = this.state.endDate;
        }

        let q = qs.parse(location.search);

        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });

        var startDate = parsed.sd != null ? parsed.sd : defaultStartDate;
        var endDate = parsed.ed != null ? parsed.ed : defaultEndDate;
        let filterGroup = parsed.fg != null ? parseInt(parsed.fg) : 1;
        let display = parsed.display != null ? parsed.display.map(d => parseInt(d)) : [1, 2];

        this.setState({
            startDate,
            endDate,
            filterGroup,
            display
        }, () => {
            this.loadData(startDate, endDate);
        });
    }

    loadData = (startDate: string, endDate: string) => {
        let { filterGroup, organTypes } = this.state;
        let organType = organTypes.length == 2 ? null : organTypes[0];

        this.props.showLoading('Loading report data, one moment please...').then(() => {
            this.props.getCenterBillingReport(startDate, endDate, filterGroup, organType)
                .then((response: ICenterBillingReportResponse) => {
                    this.setState({
                        report: response.data.results,
                        startDate: startDate,
                        endDate: endDate,
                        organTypes: organTypes
                    }, () => {
                        this.setUrlParameters();
                    });
                })
                .catch((ex) => {
                    alert('An error occured while loading report data');
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    validate = () => {
        if (this.state.startDate == '' || this.state.endDate == '') {
            return false;
        }

        const yearRegex = /^[1-9]\d{3}-\d{2}-\d{2}$/;

        if (!yearRegex.test(this.state.startDate) || !yearRegex.test(this.state.endDate)) {
            return false;
        }

        let startDate = moment(this.state.startDate, 'YYYY-MM-DD', true);
        let endDate = moment(this.state.endDate, 'YYYY-MM-DD', true);

        if (startDate.isValid() == false || endDate.isValid() == false) {
            return false;
        }

        if (startDate.isAfter(endDate)) {
            return false;
        }

        return true;
    }

    getLowEpletMM = (results: ICenterBillingReport[]) => {
        let lowEplet = 0;
        let total = 0;
        let actualMed = 0;

        results.forEach((item, index) => {
            if (item.singleEpletMmRisk && item.singleEpletMmRisk.singleEpletRisk != -1) {
                if (item.singleEpletMmRisk.singleEpletRiskLow < item.singleEpletMmRisk.singleEpletRisk && item.singleEpletMmRisk.singleEpletRiskLow == EpletRiskCutoff.Low)
                    ++actualMed;

                ++total;
                if (item.singleEpletMmRisk.singleEpletRiskLow <= EpletRiskCutoff.Low)
                    ++lowEplet;
            }
        });

        lowEplet = lowEplet - actualMed;

        return total <= 0 ? 0 : Math.round(lowEplet / total * 100);
    }

    setUrlParameters = () => {
        let { startDate, endDate, filterGroup, display } = this.state;

        let paramList = [];

        if (startDate != '') {
            paramList.push('sd=' + startDate);
        }

        if (endDate != '') {
            paramList.push('ed=' + endDate);
        }

        paramList.push('fg=' + filterGroup);

        for (let n = 0; n < display.length; n++) {
            paramList.push('display[]=' + display[n]);
        }

        let params = paramList.join('&');

        let newUrl = '/report/center-billing?' + params

        window.history.pushState({}, '', newUrl);
    }

    handleSearch = () => {
        let isValid = this.validate();

        this.setState({
            showDateWarning: isValid == false
        }, () => {
            if (isValid) {
                this.loadData(this.state.startDate, this.state.endDate);
            }
        });
    }

    handleChangeOrganType = (values: OrganType | OrganType[]) => {
        let safeValues = Array.isArray(values) ? values : [values];
        if (safeValues.length == 0) return;
        this.setState({ organTypes: safeValues }, () => this.loadData(this.state.startDate, this.state.endDate));
    }


    handleDateChange = (name: string, value: string) => {
        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    handleRadioFilterChange = (filterGroup: number) => {
        this.setState({
            filterGroup
        }, () => {
            this.loadData(this.state.startDate, this.state.endDate);
        });
    }

    handleDisplayChange = (display: any) => {
        this.setState({
            display
        }, () => {
            this.setUrlParameters();
        });
    }

    handleExport = () => {
        let params: CsvExportParams = {
            columnGroups: true,
            fileName: 'Center-Billing-Report.csv',
            skipPinnedTop: true,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.field == 'organType')
                    return params.value == 0 ? 'Kidney' : params.value == 1 ? 'Liver' : params.value;

                if (colDef.headerName == 'Rank') {
                    return params.node.rowIndex + 1;
                }
                else if (colDef.colId == 'txpDate') {
                    return params.value != '' ? moment(params.value).format('MM/DD/YYYY') : '';
                }
                else if (colDef.colId == 'invoiceDate') {
                    return params.value != '' ? moment(params.value).format('MM/DD/YYYY') : '';
                }
                else if (colDef.colId == 'micrositeActivationDate') {
                    return params.value ? moment(params.value).format('MM/DD/YYYY') : '';
                }
                else if (colDef.colId == 'chainId') {
                    if (params.value == 'null-null-null')
                        return "";
                    return ` ` + params.value;
                }
                else if (colDef.colId == 'emm') {
                    let data = params.node.data as ICenterBillingReport
                    let displayLabel = '-?-';

                    if (data.singleEpletMmRisk != null) {
                        let { singleEpletMmDr, singleEpletMmDq } = data.singleEpletMmRisk;

                        if (singleEpletMmDr != null && singleEpletMmDq != null) {
                            if (singleEpletMmDr == 0 && singleEpletMmDq == 0) {
                                displayLabel = '0 MM';
                            } else if (singleEpletMmDr < 7 && singleEpletMmDq < 9) {
                                displayLabel = 'Low';
                            } else if (singleEpletMmDq >= 15) {
                                displayLabel = 'High';
                            } else {
                                displayLabel = 'Med';
                            }
                        }
                    }

                    return displayLabel;
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    handleToggleColumnFiltering = () => {
        this.setState({
            enableColumnFiltering: !this.state.enableColumnFiltering
        });
    }

    getFilteredData = (): ICenterBillingReport[] => {
        let list = this.state.report.filter(x => {
            let groupCompletedfilter = false;
            let groupPendingfilter = false;

            if (this.state.display.indexOf(1) >= 0) {
                groupCompletedfilter = x.isTransplanted == true;
            }

            if (this.state.display.indexOf(2) >= 0) {
                groupPendingfilter = x.isTransplanted != true;
            }

            return groupCompletedfilter || groupPendingfilter;
        });

        return list;
    }

    render = () => {
        const filteredData = this.getFilteredData();
        const epletMM = this.getLowEpletMM(filteredData);
        const facilitatedTxps = filteredData.length;

        return (
            <>
                <HeaderWrapper>
                    <div className='billing-header'>
                        Center Billing Report
                        <Button bsStyle='success' style={{ float: 'right' }} onClick={this.handleExport}>Export CSV</Button>
                        <Button
                            bsStyle={this.state.enableColumnFiltering ? 'primary' : 'default'}
                            style={{ float: 'right', marginRight: 10 }}
                            onClick={this.handleToggleColumnFiltering}
                        >
                            Column Filtering {this.state.enableColumnFiltering ? 'On' : 'Off'}
                        </Button>
                    </div>

                    <Filter
                        startDate={this.state.startDate}
                        endDate={this.state.endDate}
                        filterGroup={this.state.filterGroup}
                        display={this.state.display}
                        organTypes={this.state.organTypes}
                        epletMM={epletMM}
                        facilitatedTxps={facilitatedTxps}
                        showDateWarning={this.state.showDateWarning}
                        onDateChange={this.handleDateChange}
                        onSearch={this.handleSearch}
                        onRadioFilterChange={this.handleRadioFilterChange}
                        onDisplayChange={this.handleDisplayChange}
                        onChangeOrganType={this.handleChangeOrganType}
                    />
                </HeaderWrapper>

                <Grid
                    data={filteredData}
                    enableColumnFiltering={this.state.enableColumnFiltering}
                    onGridReady={this.onGridReady} />
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        reports: appState.reports
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getCenterBillingReport: (startDate, endDate, filterGroup, organType) => {
            return dispatch(ReportActionCreators.getCenterBillingReport(startDate, endDate, filterGroup, organType));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterBillingReport);
